import { useEffect, useRef, type MutableRefObject, type ReactNode } from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { BoundAction } from '@/shared/lib/hotkeys';
import { HotkeyTip } from '@/shared/ui/hotkey';
import { useGridNavigation } from './useGridNavigation';

export interface LinesGridColumn<L> {
  key: string;
  title: string;
  width?: number;
  align?: 'left' | 'right' | 'center';
  /** `colIndex` передаётся в navRow/navCol редактируемых полей (IntInput/MoneyInput). */
  render: (line: L, rowIndex: number, colIndex: number) => ReactNode;
}

/** Что окно-владелец может сделать с таблицей с клавиатуры (Insert, F8, переходы фокуса). */
export interface LinesGridApi {
  /** Фокус в ячейку (`col` 0 — первая); обёртка-ячейка передаёт его первому полю внутри. */
  focusCell: (row: number, col: number) => void;
  /** Строка, в которой сейчас фокус, или `null`. */
  focusedRow: () => number | null;
  /** Insert: добавить строку (через `onAddRow`, как Enter в последнем поле) и встать в её первую
   *  ячейку. */
  addRow: () => void;
  /** F8: удалить строку, в которой стоит фокус, и встать на строку с тем же номером (или на
   *  последнюю, если удалили последнюю). Вне таблицы ничего не делает. */
  removeFocusedRow: () => void;
}

export interface LinesGridProps<L> {
  lines: L[];
  columns: LinesGridColumn<L>[];
  getRowKey: (line: L) => string;
  /** `false` в ответе — строку не добавили (например, последняя и так пустая): фокус на «новую»
   *  строку тогда не переводится. */
  onAddRow: () => void | boolean;
  onRemoveRow: (key: string) => void;
  readOnly?: boolean;
  rowClassName?: (line: L) => string | undefined;
  footer?: ReactNode;
  /** Позволяет вызывающему коду управлять таблицей: перевести фокус на конкретную ячейку (например,
   *  в «Коробок» сразу после выбора товара, §5), добавить или удалить строку по горячей клавише. */
  gridApiRef?: MutableRefObject<LinesGridApi | null>;
  /** Крестик удаления строки — по умолчанию последней колонкой, `'start'` переносит его первой
   *  (документы: рядом с кнопкой «+ категория» слева от выбора товара). */
  removeButtonPosition?: 'start' | 'end';
  /** Действие «Удалить строку» окна-владельца: его клавиша (F8) показывается в подсказке на
   *  крестике. Без него крестик подсказывает только название. */
  removeRowAction?: BoundAction;
}

/** §1: таблица строк документа — поля горизонтально, Tab/Enter по полям, Enter в последнем поле
 *  добавляет строку. Обычный `<table>`, а не antd Table — инпут в каждой ячейке и клавиатурная
 *  навигация так устроены заметно проще. */
export function LinesGrid<L>({
  lines,
  columns,
  getRowKey,
  onAddRow,
  onRemoveRow,
  readOnly,
  rowClassName,
  footer,
  gridApiRef,
  removeButtonPosition = 'end',
  removeRowAction,
}: LinesGridProps<L>) {
  const { containerRef, onKeyDownCapture, focusPendingRow, focusRow, focusCell, focusedRow, addRowAndFocus } = useGridNavigation(onAddRow);

  useEffect(() => {
    focusPendingRow();
  }, [lines.length, focusPendingRow]);

  // Свежие значения для стабильного `gridApiRef.current`: сам объект не пересоздаётся на каждый
  // рендер, а действия читают актуальные строки и обработчики отсюда.
  const latest = useRef({ lines, getRowKey, onRemoveRow, readOnly, addRowAndFocus });
  useEffect(() => {
    latest.current = { lines, getRowKey, onRemoveRow, readOnly, addRowAndFocus };
  });

  useEffect(() => {
    if (!gridApiRef) return;
    gridApiRef.current = {
      focusCell: (row, col) => void focusCell(row, col),
      focusedRow,
      addRow: () => {
        const { lines: current, readOnly: locked, addRowAndFocus: add } = latest.current;
        if (!locked) add(current.length);
      },
      removeFocusedRow: () => {
        const { lines: current, getRowKey: keyOf, onRemoveRow: remove, readOnly: locked } = latest.current;
        const row = focusedRow();
        const line = row === null ? undefined : current[row];
        if (locked || row === null || line === undefined) return;
        remove(keyOf(line));
        const target = Math.max(0, Math.min(row, current.length - 2));
        requestAnimationFrame(() => focusRow(target));
      },
    };
    return () => {
      gridApiRef.current = null;
    };
  }, [gridApiRef, focusRow, focusCell, focusedRow]);

  return (
    <div ref={containerRef} onKeyDownCapture={onKeyDownCapture} style={{ overflowX: 'auto' }}>
      <table className="lines-grid">
        <thead>
          <tr>
            {readOnly || removeButtonPosition !== 'start' ? null : <th style={{ width: 28 }} />}
            {columns.map((col) => (
              <th key={col.key} style={{ width: col.width, textAlign: col.align ?? 'left' }}>
                {col.title}
              </th>
            ))}
            {readOnly || removeButtonPosition !== 'end' ? null : <th style={{ width: 28 }} />}
          </tr>
        </thead>
        <tbody>
          {lines.map((line, rowIndex) => {
            /* Вне порядка Tab: иначе фокус попадает на крестик рядом с полем, и следующий Enter
               или пробел удаляет только что введённую строку (§1). */
            const removeCell = readOnly ? null : (
              <td key="remove">
                <HotkeyTip action={removeRowAction} label={removeRowAction?.label ?? 'Удалить строку'} display="inline-flex">
                  <Button
                    type="text"
                    danger
                    size="small"
                    tabIndex={-1}
                    aria-label="Удалить строку"
                    icon={<CloseOutlined />}
                    onClick={() => onRemoveRow(getRowKey(line))}
                  />
                </HotkeyTip>
              </td>
            );
            return (
              <tr key={getRowKey(line)} className={rowClassName?.(line)}>
                {removeButtonPosition === 'start' ? removeCell : null}
                {columns.map((col, colIndex) => (
                  <td key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                    {col.render(line, rowIndex, colIndex)}
                  </td>
                ))}
                {removeButtonPosition === 'end' ? removeCell : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
